import React, { useState } from "react";
import {
  DEFAULT_MODULES, STATUS_MAP, newEntity, IDEA_SUBTYPES,
  readAsDataUrl, downloadDataUrl, downloadText,
} from "../../lib/gamecraft";
import { GlassCard, StatusDot, StatusSelect, TagInput, FileDropzone, ImageCropper } from "../gc/ui";
import { Plus, Trash, Download, Sparkle, PencilSimple, X } from "@phosphor-icons/react";
import { ai } from "../../lib/api";
import { toast } from "sonner";
import { useI18n } from "../../lib/i18n";

export default function IdeasPanel({ project, onChange, onJump }) {
  const { lang } = useI18n();
  const modules = project.idea_modules?.length ? project.idea_modules : DEFAULT_MODULES;
  const entities = project.entities || {};
  const enabled = modules.filter((m) => m.enabled !== false);
  const [active, setActive] = useState(enabled[0]?.id || "characters");
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [newMod, setNewMod] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);

  const list = entities[active] || [];
  const filtered = list.filter((e) => {
    const okStatus = filter === "all" || e.status === filter;
    const okQ = !q || `${e.name} ${e.role} ${(e.tags || []).join(" ")}`.toLowerCase().includes(q.toLowerCase());
    return okStatus && okQ;
  });

  const setModules = (next) => onChange({ idea_modules: next });
  const setList = (next) => onChange({ entities: { ...entities, [active]: next } });

  const addItem = () => {
    if (project.config?.scope_frozen) return toast.error("Scope freeze: no entran ideas nuevas");
    const item = newEntity({ name: lang === "es" ? "Nueva idea" : "New idea" });
    setList([...list, item]);
    setEditing(item.id);
  };

  const updateItem = (id, patch) => setList(list.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const removeItem = (id) => setList(list.filter((e) => e.id !== id));

  const addModule = () => {
    if (project.config?.scope_frozen) return toast.error("Scope freeze: no entran módulos nuevos");
    const name = newMod.trim();
    if (!name) return;
    const id = name.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "") || `mod_${Date.now()}`;
    if (modules.some((m) => m.id === id)) return toast.error("Ese módulo ya existe");
    setModules([...modules, { id, name, enabled: true, color: "#6366F1", icon: "Cube", subcategories: [] }]);
    onChange({ entities: { ...entities, [id]: [] } });
    setNewMod("");
    setActive(id);
  };

  const toggleModule = (id) => setModules(modules.map((m) => (m.id === id ? { ...m, enabled: m.enabled === false } : m)));
  const removeModule = (id) => {
    if (!window.confirm("¿Quitar este módulo? Las ideas se conservan ocultas.")) return;
    setModules(modules.map((m) => (m.id === id ? { ...m, enabled: false } : m)));
    if (active === id) setActive(enabled.find((m) => m.id !== id)?.id || "characters");
  };

  const addSub = (name) => {
    const mod = modules.find((m) => m.id === active);
    const subs = [...(mod.subcategories || []), name];
    setModules(modules.map((m) => (m.id === active ? { ...m, subcategories: subs } : m)));
  };

  const suggest = async () => {
    setAiBusy(true);
    try {
      const mod = modules.find((m) => m.id === active);
      const res = await ai.ideas({
        prompt: `Sugiere 4 ideas concretas para el módulo "${mod?.name}" del juego 2D "${project.name}" (${project.genre}). Idea: ${project.gdd?.idea || "sin idea"}. Devuelve nombre + rol + 1 línea.`,
        category: active,
        language: lang,
      });
      toast.success("Sugerencias listas — revisa el chat de IA o pégalos como ideas.");
      const lines = (res.text || "").split("\n").filter((l) => l.trim().length > 3).slice(0, 4);
      const items = lines.map((l) => newEntity({ name: l.replace(/^[-*\d.\s]+/, "").slice(0, 48), notes: l, status: "thinking", tags: [active] }));
      setList([...list, ...items]);
    } catch {
      toast.error("No se pudo consultar la IA");
    } finally {
      setAiBusy(false);
    }
  };

  const exportJson = () => downloadText(JSON.stringify(list, null, 2), `${active}.json`, "application/json");

  const importJson = async (files) => {
    const text = await files[0].text();
    try {
      const parsed = JSON.parse(text);
      const arr = Array.isArray(parsed) ? parsed : [parsed];
      setList([...list, ...arr.map((x) => newEntity(x))]);
    } catch {
      toast.error("JSON inválido");
    }
  };

  const current = list.find((e) => e.id === editing);
  const subs = modules.find((m) => m.id === active)?.subcategories || [];

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">Ideas del proyecto</h2>
          <p className="text-sm" style={{ color: "var(--gc-muted)" }}>Módulos, subcategorías, etiquetas y estados de aprobación.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="gc-btn gc-btn-ghost h-9 px-3" onClick={suggest} disabled={aiBusy}><Sparkle size={14} /> {aiBusy ? "..." : "Sugerir con IA"}</button>
          <FileDropzone accept="application/json" onFiles={importJson} label="Importar JSON" />
          <button className="gc-btn gc-btn-ghost h-9 px-3" onClick={exportJson}><Download size={14} /> Exportar</button>
          <button className="gc-btn gc-btn-primary h-9 px-3" onClick={addItem}><Plus size={14} weight="bold" /> Nueva idea</button>
        </div>
      </div>

      <GlassCard className="p-3">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {modules.filter((m) => m.enabled !== false).map((m) => (
            <button
              key={m.id}
              onClick={() => { setActive(m.id); setEditing(null); }}
              className="shrink-0 px-3 py-2 rounded-2xl text-left min-w-[120px] border transition"
              style={{
                borderColor: active === m.id ? m.color : "var(--gc-border)",
                background: active === m.id ? `${m.color}18` : "transparent",
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold">{m.name}</span>
                <span className="text-[10px] font-bold" style={{ color: m.color }}>{(entities[m.id] || []).length}</span>
              </div>
              <div className="flex gap-1 mt-1">
                {["approved", "thinking", "cut", "neutral"].map((s) => {
                  const n = (entities[m.id] || []).filter((e) => (e.status || "neutral") === s).length;
                  return n ? <StatusDot key={s} status={s} size={6} /> : null;
                })}
              </div>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: "1px solid var(--gc-border)" }}>
          <input className="gc-input h-9 px-3 text-sm flex-1 max-w-xs" placeholder="Nuevo módulo…" value={newMod} onChange={(e) => setNewMod(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addModule()} />
          <button className="gc-btn gc-btn-ghost h-9 px-3" onClick={addModule}><Plus size={14} /> Añadir módulo</button>
          <button className="gc-btn gc-btn-ghost h-9 px-3 text-red-500" onClick={() => removeModule(active)}><Trash size={14} /> Quitar actual</button>
        </div>
      </GlassCard>

      <div className="flex flex-wrap items-center gap-2">
        <input className="gc-input h-9 px-3 text-sm w-56" placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="gc-input h-9 px-3 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">Todos los estados</option>
          {Object.values(STATUS_MAP).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <input
          className="gc-input h-9 px-3 text-sm w-48"
          placeholder="Nueva subcategoría…"
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.currentTarget.value.trim()) {
              addSub(e.currentTarget.value.trim());
              e.currentTarget.value = "";
            }
          }}
        />
        {subs.map((s) => <span key={s} className="gc-chip">{s}</span>)}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((e) => {
          const sprite = (project.sprites || []).find((s) => s.id === e.linked_sprite_id);
          return (
            <GlassCard key={e.id} hover className="p-0 overflow-hidden">
              <div className="h-1.5" style={{ background: STATUS_MAP[e.status || "neutral"]?.color }} />
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-14 h-14 rounded-xl overflow-hidden flex items-center justify-center" style={{ background: "var(--gc-bg-accent)" }}>
                    {sprite ? (
                      <img src={sprite.data_url} alt="" className="w-full h-full object-contain" style={{ imageRendering: "pixelated" }} />
                    ) : (
                      <span className="text-xs" style={{ color: "var(--gc-faint)" }}>—</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{e.name}</div>
                    <div className="text-xs truncate" style={{ color: "var(--gc-muted)" }}>{e.role || "Sin rol"}{e.subtype ? ` · ${e.subtype}` : ""} · HP {e.hp ?? 100}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(e.tags || []).slice(0, 3).map((t) => <span key={t} className="gc-chip">#{t}</span>)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <StatusSelect value={e.status || "neutral"} onChange={(s) => updateItem(e.id, { status: s })} />
                  <div className="flex gap-1">
                    <button className="gc-btn gc-btn-ghost h-8 px-2" onClick={() => setEditing(e.id)}><PencilSimple size={14} /></button>
                    <button className="gc-btn gc-btn-ghost h-8 px-2 text-red-500" onClick={() => removeItem(e.id)}><Trash size={14} /></button>
                  </div>
                </div>
              </div>
            </GlassCard>
          );
        })}
        {filtered.length === 0 && (
          <GlassCard className="col-span-full py-12 text-center text-sm" style={{ color: "var(--gc-muted)" }}>
            Vacío. Crea una idea o pide sugerencias a la IA.
          </GlassCard>
        )}
      </div>

      {current && (
        <EntityEditor
          item={current}
          project={project}
          subs={subs}
          moduleId={active}
          onJump={onJump}
          onClose={() => setEditing(null)}
          onChange={(patch) => updateItem(current.id, patch)}
          onCropSrc={setCropSrc}
          onProjectChange={onChange}
        />
      )}
      {cropSrc && (
        <ImageCropper
          src={cropSrc.src}
          onClose={() => setCropSrc(null)}
          onCrop={(out) => {
            const atts = [...(current?.attachments || []), { id: crypto.randomUUID(), name: "recorte.png", type: "image", data_url: out }];
            updateItem(current.id, { attachments: atts });
            setCropSrc(null);
          }}
        />
      )}
    </div>
  );
}

function EntityEditor({ item, project, subs, moduleId, onChange, onClose, onJump, onCropSrc, onProjectChange }) {
  const sprites = project.sprites || [];
  const files = project.files || [];
  const samples = project.samples || [];

  const attach = async (filesList) => {
    const atts = [...(item.attachments || [])];
    for (const f of filesList) {
      const data_url = await readAsDataUrl(f);
      atts.push({ id: crypto.randomUUID(), name: f.name, type: f.type, data_url });
    }
    onChange({ attachments: atts });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-3">
      <div className="gc-card w-full max-w-2xl max-h-[90vh] overflow-auto p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg">Editar idea</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Nombre"><input className="gc-input h-10 px-3 w-full" value={item.name} onChange={(e) => onChange({ name: e.target.value })} /></Field>
          <Field label="Rol / tipo"><input className="gc-input h-10 px-3 w-full" value={item.role || ""} onChange={(e) => onChange({ role: e.target.value })} /></Field>
          <Field label="Tipo concreto">
            <select className="gc-input h-10 px-3 w-full" value={item.subtype || ""} onChange={(e) => onChange({ subtype: e.target.value })}>
              <option value="">—</option>
              {(IDEA_SUBTYPES[moduleId] || []).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Subcategoría">
            <select className="gc-input h-10 px-3 w-full" value={item.subcategory || ""} onChange={(e) => onChange({ subcategory: e.target.value })}>
              <option value="">—</option>
              {subs.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Estado"><StatusSelect value={item.status || "neutral"} onChange={(s) => onChange({ status: s })} /></Field>
          <Field label="HP"><input type="number" className="gc-input h-10 px-3 w-full" value={item.hp ?? 100} onChange={(e) => onChange({ hp: +e.target.value })} /></Field>
          <Field label="Daño"><input type="number" className="gc-input h-10 px-3 w-full" value={item.damage ?? 10} onChange={(e) => onChange({ damage: +e.target.value })} /></Field>
          <Field label="Defensa"><input type="number" className="gc-input h-10 px-3 w-full" value={item.defense ?? 4} onChange={(e) => onChange({ defense: +e.target.value })} /></Field>
          <Field label="Velocidad"><input type="number" className="gc-input h-10 px-3 w-full" value={item.speed ?? 4} onChange={(e) => onChange({ speed: +e.target.value })} /></Field>
        </div>
        <Field label="Notas" className="mt-3">
          <textarea className="gc-input w-full p-3 text-sm min-h-[90px]" value={item.notes || ""} onChange={(e) => onChange({ notes: e.target.value })} />
        </Field>
        <Field label="Etiquetas" className="mt-3">
          <TagInput tags={item.tags || []} onChange={(tags) => onChange({ tags })} />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <Field label="Sprite">
            <select className="gc-input h-10 px-3 w-full" value={item.linked_sprite_id || ""} onChange={(e) => onChange({ linked_sprite_id: e.target.value })}>
              <option value="">—</option>
              {sprites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Código">
            <select className="gc-input h-10 px-3 w-full" value={item.linked_file_id || ""} onChange={(e) => onChange({ linked_file_id: e.target.value })}>
              <option value="">—</option>
              {files.map((f) => <option key={f.id} value={f.id}>{f.path}</option>)}
            </select>
          </Field>
          <Field label="Audio">
            <select className="gc-input h-10 px-3 w-full" value={item.linked_audio_id || ""} onChange={(e) => onChange({ linked_audio_id: e.target.value })}>
              <option value="">—</option>
              {samples.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <FileDropzone accept="image/*,audio/*,.js,.py,.gd,.cs,.json" multiple onFiles={attach} label="Adjuntar archivo" />
          {item.linked_sprite_id && <button className="gc-btn gc-btn-ghost h-9 px-3 text-xs" onClick={() => onJump?.("sprites")}>Abrir sprite</button>}
          {item.linked_file_id && <button className="gc-btn gc-btn-ghost h-9 px-3 text-xs" onClick={() => onJump?.("code")}>Abrir código</button>}
          <button
            className="gc-btn gc-btn-primary h-9 px-3 text-xs"
            onClick={() => {
              const playtest = { ...(project.config?.playtest || {}) };
              if (item.linked_sprite_id) {
                const spr = sprites.find((s) => s.id === item.linked_sprite_id);
                const isGear = spr?.studio === "gear" || /weapon|arma|item|potion/i.test(`${spr?.category || ""} ${item.subtype || ""} ${moduleId}`);
                playtest[isGear ? "weaponId" : "creatureId"] = item.linked_sprite_id;
              }
              onProjectChange?.({ config: { ...(project.config || {}), playtest } });
              onJump?.("engine");
            }}
          >
            Probar en el motor
          </button>
        </div>
        {(item.attachments || []).length > 0 && (
          <div className="mt-3 space-y-1">
            {item.attachments.map((a) => (
              <div key={a.id} className="flex items-center gap-2 text-xs">
                <span className="truncate flex-1">{a.name}</span>
                {a.type?.startsWith("image") && (
                  <button className="gc-chip" onClick={() => onCropSrc({ src: a.data_url })}>Recortar</button>
                )}
                <button className="gc-chip" onClick={() => downloadDataUrl(a.data_url, a.name)}>Descargar</button>
                <button className="text-red-500" onClick={() => onChange({ attachments: item.attachments.filter((x) => x.id !== a.id) })}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, className }) {
  return (
    <label className={className}>
      <div className="text-[11px] font-semibold mb-1" style={{ color: "var(--gc-muted)" }}>{label}</div>
      {children}
    </label>
  );
}
